import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../layouts/Header';
import Footer from '../layouts/Footer';
import { Badge, Button, Card, Col, Dropdown, Form, InputGroup, Nav, ProgressBar, Row, Table } from 'react-bootstrap';
import axiosInstance from '../services/axiosInterceptor';
import useStudyYearSelector from './useStudyYearSelector';

function scoreVariant(pts) {
  const n = Number(pts);
  if (n >= 80) return 'success';
  if (n >= 60) return 'warning';
  return 'danger';
}

const STATUS_META = {
  assigned:  { label: 'รอทำ / กำลังทำ', variant: 'warning' },
  completed: { label: 'ทำเสร็จแล้ว',     variant: 'success' },
  cancelled: { label: 'ยกเลิก',          variant: 'secondary' },
};

const todayISO = () => new Date().toISOString().slice(0, 10);
const thaiDate = (value) => (value ? new Date(value).toLocaleDateString('th-TH') : '-');

const emptyForm = () => ({ activityId: '', addPoints: '', activityDate: todayISO(), status: 'assigned', note: '' });

export default function DisciplineMerit() {
  const setSkin = useState(localStorage.getItem('skin-mode') ? 'dark' : '')[1];
  const { yearList, yearId, yearLabel, handleSelectYear } = useStudyYearSelector();

  const [tab,           setTab]           = useState('student');
  const [activities,    setActivities]    = useState([]);
  const [message,       setMessage]       = useState('');
  const [error,         setError]         = useState('');

  // แท็บ "บันทึกรายบุคคล"
  const [keyword,       setKeyword]       = useState('');
  const [students,      setStudents]      = useState([]);
  const [selected,      setSelected]      = useState(null);
  const [detail,        setDetail]        = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [form,          setForm]          = useState(emptyForm);
  const [saving,        setSaving]        = useState(false);
  const [mobileView,    setMobileView]    = useState('list');

  // แท็บ "งานที่มอบหมายทั้งหมด"
  const [queueStatus,   setQueueStatus]   = useState('assigned');
  const [queueKeyword,  setQueueKeyword]  = useState('');
  const [queue,         setQueue]         = useState({ rows: [], statusCount: {}, pagination: { page: 1, totalPages: 1, total: 0 } });
  const [queuePage,     setQueuePage]     = useState(1);
  const [queueLoading,  setQueueLoading]  = useState(false);

  useEffect(() => {
    if (!message) return;
    const t = setTimeout(() => setMessage(''), 4000);
    return () => clearTimeout(t);
  }, [message]);

  useEffect(() => {
    if (!error) return;
    const t = setTimeout(() => setError(''), 6000);
    return () => clearTimeout(t);
  }, [error]);

  useEffect(() => {
    axiosInstance.get('/api/discipline/conduct-activities')
      .then(res => setActivities(res.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setSelected(null);
    setDetail(null);
    setMobileView('list');
  }, [yearId]);

  const search = useCallback((e) => {
    e?.preventDefault();
    if (!yearId) return;
    setError('');
    axiosInstance
      .get(`/api/discipline/conduct/students?yearId=${yearId}&keyword=${encodeURIComponent(keyword.trim())}`)
      .then(res => setStudents(res.data || []))
      .catch(() => setError('ไม่สามารถค้นหานักเรียนได้'));
  }, [yearId, keyword]);

  useEffect(() => { if (yearId) search(); }, [yearId, search]);

  const loadDetail = useCallback((student) => {
    setSelected(student);
    setDetail(null);
    setDetailLoading(true);
    setMobileView('detail');
    setMessage('');
    setError('');
    axiosInstance
      .get(`/api/discipline/conduct/${student.st_id}/${yearId}`)
      .then(res => setDetail(res.data))
      .catch(() => setError('ไม่สามารถโหลดข้อมูลนักเรียนได้'))
      .finally(() => setDetailLoading(false));
  }, [yearId]);

  const loadQueue = useCallback(() => {
    if (!yearId) return;
    setQueueLoading(true);
    axiosInstance
      .get(`/api/discipline/merits?yearId=${yearId}&status=${queueStatus}&keyword=${encodeURIComponent(queueKeyword.trim())}&page=${queuePage}`)
      .then(res => setQueue(res.data))
      .catch(() => setError('ไม่สามารถโหลดรายการงานจิตอาสาได้'))
      .finally(() => setQueueLoading(false));
    // ค้นหาด้วยคำค้นจะยิงเมื่อกดปุ่มค้นหาเท่านั้น จึงไม่ผูก queueKeyword ไว้ใน deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yearId, queueStatus, queuePage]);

  useEffect(() => { if (tab === 'queue') loadQueue(); }, [tab, loadQueue]);
  useEffect(() => { setQueuePage(1); }, [queueStatus, yearId]);

  const selectedActivity = useMemo(
    () => activities.find(a => String(a.activity_id) === String(form.activityId)),
    [activities, form.activityId]
  );

  useEffect(() => {
    if (selectedActivity) setForm(prev => ({ ...prev, addPoints: selectedActivity.default_points }));
  }, [selectedActivity]);

  const saveMerit = (e) => {
    e.preventDefault();
    if (!selected || !detail?.student) return;
    setSaving(true);
    setError('');
    setMessage('');
    axiosInstance
      .post('/api/discipline/merits', {
        studentId:    selected.st_id,
        studyId:      detail.student.study_id,
        yearId,
        activityId:   form.activityId,
        addPoints:    Number(form.addPoints),
        activityDate: form.activityDate || null,
        status:       form.status,
        note:         form.note,
      })
      .then(() => {
        setMessage(form.status === 'completed'
          ? 'บันทึกงานจิตอาสาและเพิ่มคะแนนเรียบร้อยแล้ว'
          : 'มอบหมายงานจิตอาสาเรียบร้อยแล้ว');
        setForm(emptyForm());
        loadDetail(selected);
        search();
      })
      .catch(err => setError(err.response?.data?.message || 'ไม่สามารถบันทึกได้'))
      .finally(() => setSaving(false));
  };

  const changeStatus = (merit, status) => {
    const verb = status === 'completed'
      ? 'ยืนยันว่าทำเสร็จและเพิ่มคะแนน'
      : status === 'cancelled' ? 'ยกเลิกงานนี้' : 'ย้อนกลับเป็นรอทำ (คะแนนที่เพิ่มไว้จะถูกดึงคืน)';
    if (!window.confirm(`ต้องการ${verb} หรือไม่?`)) return;
    setError('');
    axiosInstance
      .put('/api/discipline/merits', { meritId: merit.merit_id, status })
      .then(() => {
        setMessage(status === 'completed' ? 'เพิ่มคะแนนความประพฤติเรียบร้อยแล้ว' : 'ปรับสถานะเรียบร้อยแล้ว');
        if (selected) loadDetail(selected);
        if (tab === 'queue') loadQueue();
        search();
      })
      .catch(err => setError(err.response?.data?.message || 'ไม่สามารถปรับสถานะได้'));
  };

  const removeMerit = (merit) => {
    if (!window.confirm('ต้องการลบรายการงานจิตอาสานี้ออกจากประวัติหรือไม่?')) return;
    axiosInstance
      .delete(`/api/discipline/merits/${merit.merit_id}`)
      .then(() => {
        setMessage('ลบรายการเรียบร้อยแล้ว');
        if (selected) loadDetail(selected);
        if (tab === 'queue') loadQueue();
        search();
      })
      .catch(err => setError(err.response?.data?.message || 'ไม่สามารถลบได้'));
  };

  const summary = detail?.summary || { deducted_points: 0, merit_points: 0, remaining_points: 100 };
  const variant = scoreVariant(summary.remaining_points);
  const merits  = detail?.merits || [];

  const statusActions = (m) => (
    <>
      {m.status === 'assigned' && (
        <>
          <Button variant="success" size="sm" className="me-1" onClick={() => changeStatus(m, 'completed')}>
            ทำเสร็จแล้ว
          </Button>
          <Button variant="outline-secondary" size="sm" onClick={() => changeStatus(m, 'cancelled')}>
            ยกเลิก
          </Button>
        </>
      )}
      {m.status === 'completed' && (
        <Button variant="outline-warning" size="sm" onClick={() => changeStatus(m, 'assigned')}>
          ยกเลิกการเพิ่มคะแนน
        </Button>
      )}
      {m.status === 'cancelled' && (
        <Button variant="outline-danger" size="sm" onClick={() => removeMerit(m)}>
          ลบ
        </Button>
      )}
    </>
  );

  return (
    <>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">

        {/* page header */}
        <div className="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-3">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item">ฝ่ายปกครอง</li>
              <li className="breadcrumb-item active">ทำความดีเพิ่มคะแนน</li>
            </ol>
            <h4 className="main-title mb-0">ทำความดี — เพิ่มคะแนนความประพฤติ</h4>
          </div>
          <Dropdown>
            <Dropdown.Toggle variant="outline-primary">
              {yearLabel || 'เลือกปีการศึกษา'}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {yearList.map(y => (
                <Dropdown.Item key={y.year_id} onClick={() => handleSelectYear(y.year_id)}>
                  ปีการศึกษา {y.year_year}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>

        <Nav variant="tabs" activeKey={tab} onSelect={k => setTab(k)} className="mb-3">
          <Nav.Item><Nav.Link eventKey="student">บันทึกรายบุคคล</Nav.Link></Nav.Item>
          <Nav.Item><Nav.Link eventKey="queue">งานที่มอบหมายทั้งหมด</Nav.Link></Nav.Item>
        </Nav>

        {message && (
          <div className="alert alert-success alert-dismissible d-flex align-items-center gap-2 mb-3" role="alert">
            ✓ {message}
            <button type="button" className="btn-close ms-auto" onClick={() => setMessage('')} />
          </div>
        )}
        {error && (
          <div className="alert alert-danger alert-dismissible d-flex align-items-center gap-2 mb-3" role="alert">
            ⚠ {error}
            <button type="button" className="btn-close ms-auto" onClick={() => setError('')} />
          </div>
        )}

        {tab === 'student' && (
          <Row className="g-3">

            {/* student list */}
            <Col xl={4} className={mobileView === 'detail' ? 'd-none d-xl-block' : ''}>
              <Card className="card-one mb-3">
                <Card.Body>
                  <Form onSubmit={search}>
                    <InputGroup>
                      <Form.Control
                        value={keyword}
                        onChange={e => setKeyword(e.target.value)}
                        placeholder="เลขประจำตัว หรือชื่อนักเรียน"
                        style={{ fontSize: 16 }}
                      />
                      <Button type="submit" variant="primary">ค้นหา</Button>
                    </InputGroup>
                  </Form>
                </Card.Body>

                <Card.Header className="d-flex align-items-center justify-content-between py-2">
                  <Card.Title as="h6" className="mb-0">รายชื่อนักเรียน</Card.Title>
                  {students.length > 0 && (
                    <Badge bg="secondary" className="fw-normal">{students.length} คน</Badge>
                  )}
                </Card.Header>
                <Card.Body className="p-0" style={{ maxHeight: '62vh', overflowY: 'auto' }}>
                  {students.length === 0 ? (
                    <div className="text-center text-secondary py-5">
                      <div style={{ fontSize: 34, opacity: 0.25 }}>🔍</div>
                      ไม่พบนักเรียน
                    </div>
                  ) : students.map(st => {
                    const isSelected = selected?.st_id === st.st_id;
                    const pts = Number(st.remaining_points);
                    return (
                      <button
                        key={st.st_id}
                        type="button"
                        onClick={() => loadDetail(st)}
                        style={{ minHeight: 60, cursor: 'pointer', transition: 'background .15s' }}
                        className={[
                          'd-flex align-items-center gap-3 w-100 text-start border-0 border-bottom px-3 py-3',
                          isSelected ? 'bg-primary text-white' : 'bg-transparent text-body',
                        ].join(' ')}
                      >
                        <div
                          style={{
                            width: 42, height: 42, fontSize: 13, flexShrink: 0,
                            border: `2px solid ${isSelected ? 'white' : 'currentColor'}`,
                          }}
                          className={[
                            'd-flex align-items-center justify-content-center rounded-circle fw-bold',
                            isSelected ? 'bg-white text-primary' : `text-${scoreVariant(pts)}`,
                          ].join(' ')}
                        >
                          {pts}
                        </div>
                        <div className="flex-grow-1 overflow-hidden">
                          <div className="fw-semibold text-truncate">{st.st_name}</div>
                          <div className={`fs-xs text-truncate ${isSelected ? 'text-white opacity-75' : 'text-secondary'}`}>
                            {st.st_idnumber} · ม.{st.c_id}/{st.r_no} · {st.p_code}
                            {Number(st.merit_points) > 0 && ` · จิตอาสา +${st.merit_points}`}
                          </div>
                        </div>
                        <span className={isSelected ? 'text-white' : 'text-secondary'} style={{ flexShrink: 0 }}>›</span>
                      </button>
                    );
                  })}
                </Card.Body>
              </Card>
            </Col>

            {/* detail */}
            <Col xl={8} className={mobileView === 'list' ? 'd-none d-xl-block' : ''}>

              <Button
                variant="outline-secondary"
                size="sm"
                className="d-xl-none mb-3"
                onClick={() => setMobileView('list')}
              >
                ← กลับรายชื่อนักเรียน
              </Button>

              {detailLoading && (
                <Card className="card-one">
                  <Card.Body className="text-center py-5">
                    <div className="spinner-border text-primary mb-3" role="status" />
                    <div className="text-secondary">กำลังโหลดข้อมูล…</div>
                  </Card.Body>
                </Card>
              )}

              {!detailLoading && !detail && (
                <Card className="card-one">
                  <Card.Body className="text-center text-secondary py-5 px-4">
                    <div style={{ fontSize: 40, opacity: 0.2 }}>🤝</div>
                    <div className="mt-2">เลือกนักเรียนจากรายชื่อด้านซ้าย<br />เพื่อมอบหมายงานจิตอาสาและเพิ่มคะแนน</div>
                  </Card.Body>
                </Card>
              )}

              {!detailLoading && detail && (
                <Card className="card-one mb-3">
                  <Card.Body>
                    <div className="d-flex align-items-center gap-3">
                      <div className="flex-grow-1">
                        <h5 className="mb-1">{detail.student.st_name}</h5>
                        <div className="text-secondary fs-sm">
                          {detail.student.st_idnumber} · ม.{detail.student.c_id}/{detail.student.r_no} · {detail.student.p_code}
                        </div>
                      </div>
                      <div className="text-center" style={{ minWidth: 80, flexShrink: 0 }}>
                        <div className={`fw-bold lh-1 text-${variant}`} style={{ fontSize: 40 }}>
                          {summary.remaining_points}
                        </div>
                        <div className="text-secondary" style={{ fontSize: 11 }}>/ 100 คะแนน</div>
                      </div>
                    </div>
                    <ProgressBar
                      now={Math.max(0, summary.remaining_points)}
                      variant={variant}
                      className="mt-3 rounded-pill"
                      style={{ height: 8 }}
                    />
                    <div className="d-flex flex-wrap justify-content-between gap-2 mt-1" style={{ fontSize: 11 }}>
                      <span className="text-danger">ตัดไปแล้ว {summary.deducted_points} คะแนน</span>
                      <span className="text-success">จิตอาสาได้คืน +{summary.merit_points || 0} คะแนน</span>
                      <span className={`fw-semibold text-${variant}`}>คงเหลือ {summary.remaining_points} คะแนน</span>
                    </div>
                  </Card.Body>

                  <Card.Header>
                    <Card.Title as="h6" className="mb-0">มอบหมาย / บันทึกงานจิตอาสา</Card.Title>
                  </Card.Header>
                  <Card.Body>
                    {activities.length === 0 ? (
                      <div className="alert alert-warning mb-0">
                        ยังไม่มีรายการงานจิตอาสา —{' '}
                        <Link to="/discipline/merit-activities">ไปเพิ่มรายการงานจิตอาสา</Link>
                      </div>
                    ) : (
                      <Form onSubmit={saveMerit}>
                        <Row className="g-3">
                          <Col xs={12} md={5}>
                            <Form.Label className="fw-semibold mb-1">
                              งานจิตอาสา <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Select
                              value={form.activityId}
                              onChange={e => setForm({ ...form, activityId: e.target.value })}
                              required
                              style={{ fontSize: 16 }}
                            >
                              <option value="">— เลือกงานจิตอาสา —</option>
                              {activities.map(a => (
                                <option key={a.activity_id} value={a.activity_id}>
                                  {a.activity_name} (+{a.default_points} คะแนน)
                                </option>
                              ))}
                            </Form.Select>
                          </Col>
                          <Col xs={5} md={2}>
                            <Form.Label className="fw-semibold mb-1">
                              คะแนนที่เพิ่ม <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                              type="number" min="1" max="100"
                              value={form.addPoints}
                              onChange={e => setForm({ ...form, addPoints: e.target.value })}
                              required
                              style={{ fontSize: 16 }}
                            />
                          </Col>
                          <Col xs={7} md={5}>
                            <Form.Label className="fw-semibold mb-1">วันที่ทำงานจิตอาสา</Form.Label>
                            <Form.Control
                              type="date"
                              value={form.activityDate}
                              onChange={e => setForm({ ...form, activityDate: e.target.value })}
                              style={{ fontSize: 16 }}
                            />
                          </Col>
                          <Col xs={12} md={5}>
                            <Form.Label className="fw-semibold mb-1">สถานะ</Form.Label>
                            <Form.Select
                              value={form.status}
                              onChange={e => setForm({ ...form, status: e.target.value })}
                              style={{ fontSize: 16 }}
                            >
                              <option value="assigned">มอบหมายให้ไปทำ (ยังไม่เพิ่มคะแนน)</option>
                              <option value="completed">ทำเสร็จแล้ว (เพิ่มคะแนนทันที)</option>
                            </Form.Select>
                          </Col>
                          <Col xs={12} md={7}>
                            <Form.Label className="fw-semibold mb-1">หมายเหตุ</Form.Label>
                            <Form.Control
                              value={form.note}
                              placeholder="รายละเอียดงาน / ครูผู้ควบคุม"
                              onChange={e => setForm({ ...form, note: e.target.value })}
                              style={{ fontSize: 16 }}
                            />
                          </Col>
                          <Col xs={12}>
                            <Button
                              type="submit" variant="success"
                              className="w-100 py-2 fw-semibold"
                              disabled={saving}
                              style={{ fontSize: 16 }}
                            >
                              {saving
                                ? <><span className="spinner-border spinner-border-sm me-2" role="status" />กำลังบันทึก…</>
                                : form.status === 'completed' ? 'บันทึกและเพิ่มคะแนน' : 'มอบหมายงานจิตอาสา'}
                            </Button>
                          </Col>
                        </Row>
                      </Form>
                    )}
                  </Card.Body>
                </Card>
              )}
            </Col>

            {/* ประวัติจิตอาสาของนักเรียนที่เลือก */}
            {detail && !detailLoading && (
              <Col xs={12} className={mobileView === 'list' ? 'd-none d-xl-block' : ''}>
                <Card className="card-one">
                  <Card.Header className="d-flex align-items-center">
                    <Card.Title as="h6" className="mb-0">
                      ประวัติงานจิตอาสา — {detail.student.st_name}
                    </Card.Title>
                    <Badge bg="success" className="ms-auto fw-normal">
                      ได้คืนแล้ว +{summary.merit_points || 0} คะแนน
                    </Badge>
                  </Card.Header>
                  <Card.Body className="p-0">

                    {/* desktop table */}
                    <div className="d-none d-md-block" style={{ overflowX: 'auto' }}>
                      <Table hover className="mb-0">
                        <thead className="table-secondary">
                          <tr>
                            <th style={{ width: 110 }}>วันที่</th>
                            <th>งานจิตอาสา</th>
                            <th>หมายเหตุ</th>
                            <th>ผู้บันทึก</th>
                            <th className="text-center" style={{ width: 130 }}>สถานะ</th>
                            <th className="text-center" style={{ width: 80 }}>คะแนน</th>
                            <th className="text-center" style={{ width: 200 }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {merits.length === 0 ? (
                            <tr>
                              <td colSpan={7} className="text-center text-secondary py-4">
                                ยังไม่มีประวัติงานจิตอาสา
                              </td>
                            </tr>
                          ) : merits.map(m => (
                            <tr key={m.merit_id}>
                              <td className="text-nowrap">{thaiDate(m.activity_date || m.created_at)}</td>
                              <td>{m.activity_name_snapshot}</td>
                              <td className="text-secondary">{m.note || '-'}</td>
                              <td className="text-secondary">{m.created_by_name || '-'}</td>
                              <td className="text-center">
                                <Badge bg={STATUS_META[m.status]?.variant}>{STATUS_META[m.status]?.label}</Badge>
                              </td>
                              <td className={`text-center fw-semibold ${m.status === 'completed' ? 'text-success' : 'text-secondary'}`}>
                                +{m.add_points}
                              </td>
                              <td className="text-center text-nowrap">{statusActions(m)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>

                    {/* mobile card rows */}
                    <div className="d-md-none">
                      {merits.length === 0 ? (
                        <div className="text-center text-secondary py-4">ยังไม่มีประวัติงานจิตอาสา</div>
                      ) : merits.map(m => (
                        <div key={m.merit_id} className="px-3 py-3 border-bottom">
                          <div className="d-flex align-items-start gap-3">
                            <div className="flex-grow-1">
                              <div className="fw-semibold">{m.activity_name_snapshot}</div>
                              <div className="text-secondary mt-1" style={{ fontSize: 12 }}>
                                {thaiDate(m.activity_date || m.created_at)}
                                {m.created_by_name ? ` · ${m.created_by_name}` : ''}
                              </div>
                              {m.note && (
                                <div className="text-secondary" style={{ fontSize: 12 }}>หมายเหตุ: {m.note}</div>
                              )}
                              <Badge bg={STATUS_META[m.status]?.variant} className="mt-2">
                                {STATUS_META[m.status]?.label}
                              </Badge>
                            </div>
                            <Badge
                              bg={m.status === 'completed' ? 'success' : 'secondary'}
                              className="py-2 px-2"
                              style={{ flexShrink: 0 }}
                            >
                              +{m.add_points}
                            </Badge>
                          </div>
                          <div className="d-flex gap-2 mt-2">{statusActions(m)}</div>
                        </div>
                      ))}
                    </div>

                  </Card.Body>
                </Card>
              </Col>
            )}
          </Row>
        )}

        {tab === 'queue' && (
          <Card className="card-one">
            <Card.Body className="border-bottom">
              <Row className="g-2 align-items-end">
                <Col xs={12} md={4}>
                  <Form.Label className="fw-semibold mb-1">สถานะ</Form.Label>
                  <Form.Select value={queueStatus} onChange={e => setQueueStatus(e.target.value)} style={{ fontSize: 16 }}>
                    <option value="assigned">รอทำ / กำลังทำ ({queue.statusCount?.assigned || 0})</option>
                    <option value="completed">ทำเสร็จแล้ว ({queue.statusCount?.completed || 0})</option>
                    <option value="cancelled">ยกเลิก ({queue.statusCount?.cancelled || 0})</option>
                    <option value="all">ทั้งหมด</option>
                  </Form.Select>
                </Col>
                <Col xs={12} md={8}>
                  <Form.Label className="fw-semibold mb-1">ค้นหา</Form.Label>
                  <Form onSubmit={(e) => { e.preventDefault(); setQueuePage(1); loadQueue(); }}>
                    <InputGroup>
                      <Form.Control
                        value={queueKeyword}
                        onChange={e => setQueueKeyword(e.target.value)}
                        placeholder="เลขประจำตัว ชื่อนักเรียน หรือชื่องาน"
                        style={{ fontSize: 16 }}
                      />
                      <Button type="submit" variant="primary">ค้นหา</Button>
                    </InputGroup>
                  </Form>
                </Col>
              </Row>
            </Card.Body>
            <Card.Body className="p-0">
              {queueLoading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status" />
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <Table hover className="mb-0">
                    <thead className="table-secondary">
                      <tr>
                        <th style={{ width: 110 }}>วันที่</th>
                        <th>นักเรียน</th>
                        <th>งานจิตอาสา</th>
                        <th className="text-center" style={{ width: 130 }}>สถานะ</th>
                        <th className="text-center" style={{ width: 80 }}>คะแนน</th>
                        <th className="text-center" style={{ width: 200 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {(queue.rows || []).length === 0 ? (
                        <tr>
                          <td colSpan={6} className="text-center text-secondary py-4">ไม่มีรายการ</td>
                        </tr>
                      ) : queue.rows.map(m => (
                        <tr key={m.merit_id}>
                          <td className="text-nowrap">{thaiDate(m.activity_date || m.created_at)}</td>
                          <td>
                            <div className="fw-semibold">{m.st_name}</div>
                            <div className="text-secondary fs-xs">
                              {m.st_idnumber}{m.c_id ? ` · ม.${m.c_id}/${m.r_no}` : ''}{m.p_code ? ` · ${m.p_code}` : ''}
                            </div>
                          </td>
                          <td>
                            {m.activity_name_snapshot}
                            {m.note && <div className="text-secondary fs-xs">{m.note}</div>}
                          </td>
                          <td className="text-center">
                            <Badge bg={STATUS_META[m.status]?.variant}>{STATUS_META[m.status]?.label}</Badge>
                          </td>
                          <td className={`text-center fw-semibold ${m.status === 'completed' ? 'text-success' : 'text-secondary'}`}>
                            +{m.add_points}
                          </td>
                          <td className="text-center text-nowrap">{statusActions(m)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
            {(queue.pagination?.totalPages || 1) > 1 && (
              <Card.Footer className="d-flex align-items-center justify-content-between">
                <span className="text-secondary fs-sm">
                  หน้า {queue.pagination.page} / {queue.pagination.totalPages} ({queue.pagination.total} รายการ)
                </span>
                <div className="d-flex gap-2">
                  <Button
                    variant="outline-secondary" size="sm"
                    disabled={queue.pagination.page <= 1}
                    onClick={() => setQueuePage(p => Math.max(1, p - 1))}
                  >
                    ก่อนหน้า
                  </Button>
                  <Button
                    variant="outline-secondary" size="sm"
                    disabled={queue.pagination.page >= queue.pagination.totalPages}
                    onClick={() => setQueuePage(p => p + 1)}
                  >
                    ถัดไป
                  </Button>
                </div>
              </Card.Footer>
            )}
          </Card>
        )}

        <Footer />
      </div>
    </>
  );
}
